/**
 * Records an explicit search interaction without delaying navigation.
 *
 * This lightweight call is used only at explicit search-action boundaries:
 * fresh autocomplete selections and deliberate general-search submissions.
 * Callers must exclude search history, passive refreshes, filters, and other
 * non-search navigation.
 */
const SESSION_KEY = "alphamd.search.session";
const INTENT_KEY = "alphamd.search.intent";
const INTENT_MAX_AGE_MS = 5 * 60 * 1000;
let memorySession = "";
let memoryIntent = null;

const normalizeQuery = (value) => typeof value === "string"
  ? value.normalize("NFKC").trim().replace(/\s+/g, " ").toLocaleLowerCase("en-US") : "";

const newId = () => {
  if (typeof window === "undefined" || !window.crypto) return "";
  if (typeof window.crypto.randomUUID === "function") return window.crypto.randomUUID();
  if (typeof window.crypto.getRandomValues !== "function") return "";
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export const getSearchSessionId = () => {
  if (typeof window === "undefined") return "";
  try {
    const saved = window.sessionStorage.getItem(SESSION_KEY);
    if (saved && /^[a-f\d]{8}-[a-f\d]{4}-4[a-f\d]{3}-[89ab][a-f\d]{3}-[a-f\d]{12}$/i.test(saved)) return saved;
    memorySession ||= newId();
    if (memorySession) window.sessionStorage.setItem(SESSION_KEY, memorySession);
    return memorySession;
  } catch { memorySession ||= newId(); return memorySession; }
};

const storeIntent = (intent) => {
  memoryIntent = intent;
  try { window.sessionStorage.setItem(INTENT_KEY, JSON.stringify(intent)); } catch (_) { /* Use in-memory fallback. */ }
};

const postSearchIntent = async (url, options) => {
  const retryDelays = [250, 1000];
  for (let attempt = 0; attempt <= retryDelays.length; attempt++) {
    try {
      const response = await fetch(url, options);
      if (response.ok) return true;
      // Validation/auth/rate-limit failures need a new decision, not repeated
      // requests. Retry only temporary server failures using the same event ID.
      if (response.status < 500 || response.status > 599) return false;
    } catch (_) { /* A dropped connection may recover during SPA navigation. */ }
    if (attempt === retryDelays.length) return false;
    await new Promise(resolve => setTimeout(resolve, retryDelays[attempt]));
  }
  return false;
};

// A fresh explicit action can be consumed once by its first results request.
// Result-page refreshes, tabs, sorting, pagination, and history clicks cannot
// reuse the event after consumption, even when the query itself is unchanged.
export const consumeSearchIntent = (rawQuery, selectedTermId) => {
  if (typeof window === "undefined") return null;
  let intent = memoryIntent;
  try {
    const stored = window.sessionStorage.getItem(INTENT_KEY);
    if (stored) intent = JSON.parse(stored);
  } catch (_) { /* Use in-memory fallback. */ }
  if (!intent || intent.target !== "results" || Date.now() - intent.createdAt > INTENT_MAX_AGE_MS ||
      normalizeQuery(intent.rawQuery) !== normalizeQuery(rawQuery) ||
      (intent.selectedTermId || "") !== (selectedTermId || "").toLowerCase()) return null;
  memoryIntent = null;
  try { window.sessionStorage.removeItem(INTENT_KEY); } catch (_) { /* Already cleared in memory. */ }
  return intent.eventId;
};

export const getSearchSelectionContext = (result, source) => {
  const type = String(result?.type || "");
  const product = ["drug", "biologic", "vaccine", "otc", "device", "pipeline"].includes(type.toLowerCase());
  const diagnosis = ["diseases", "disease", "psychiatry and psychology"].includes(type.toLowerCase());
  return {
    type,
    ...(diagnosis && /^[a-f\d]{24}$/i.test(result?.id || "") && { selectedTermId: result.id.toLowerCase() }),
    ...(product && { category: type.toLowerCase() }),
    target: product ? "product" : "results",
    ...(source && { source }),
  };
};

export const logKeywordSearch = (value, context = {}) => {
  const searchTerm = typeof value === "string" ? value.trim() : "";
  const baseUrl = process.env.NEXT_PUBLIC_BASE_URL;

  if (!searchTerm || !baseUrl || typeof window === "undefined") {
    return Promise.resolve(false);
  }

  let token = "";

  try {
    const tokenCookie = document.cookie
      .split(";")
      .map((cookie) => cookie.trim())
      .find((cookie) => cookie.startsWith("token="));

    if (tokenCookie) {
      token = tokenCookie.slice("token=".length);
    }
  } catch (_) {
    // Cookie access can be unavailable in restricted browser contexts.
  }

  const headers = { "Content-Type": "application/json", ...(token && { Authorization: `Bearer ${token}` }) };
  const sessionId = getSearchSessionId();
  const eventId = newId();
  const requests = [];
  if (sessionId && eventId && searchTerm.length <= 512) {
    const intent = {
      eventId, sessionId, rawQuery: searchTerm,
      ...(context.selectedTermId && { selectedTermId: context.selectedTermId }),
      ...(context.type && { type: context.type }),
      ...(context.category && { category: context.category }),
      target: context.target || "results",
      source: context.source || window.location?.pathname || "search",
    };
    storeIntent({ ...intent, createdAt: Date.now() });
    requests.push(postSearchIntent(`${baseUrl}/products/search-intents`, {
      method: "POST", headers, body: JSON.stringify(intent), keepalive: true,
    }));
  }

  // Preserve existing authenticated location-trend semantics. Activity is a
  // separate event stream and also records guest searches.
  if (token) requests.push(fetch(`${baseUrl}/products/track-keyword-search`, {
    method: "POST",
    headers,
    body: JSON.stringify({ searchTerm }),
    keepalive: true,
  })
    .then((response) => response.ok)
    .catch(() => false));
  return Promise.all(requests).then(results => results.some(Boolean));
};

export const getKeywordForSelection = (query, result) => {
  const candidates = [
    query,
    result?.term,
    result?.name,
    result?.displayName,
    result?.openfda?.brand_name,
    result?.openfda?.generic_name,
  ];

  const keyword = candidates.find(
    (candidate) => typeof candidate === "string" && candidate.trim()
  );

  return keyword?.trim() || "";
};
